import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Result, Button, notification } from 'antd';
import ssoAuth from '../utils/ssoAuth';

const SSOCallback = () => {
  const [status, setStatus] = useState('正在处理SSO登录...');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // 使用ref防止重复执行
  const hasProcessed = useRef(false);

  useEffect(() => {
    // 获取授权码
    const code = searchParams.get('code');
    if (!code) {
      setError('未收到授权码');
      setLoading(false);
      return;
    }

    // 使用sessionStorage作为全局锁，防止重复处理
    const processKey = `sso_processing_${code}`;
    const notifyKey = `sso_notified_${code}`;
    
    // 防止重复处理（React严格模式下useEffect可能执行两次）
    if (hasProcessed.current || sessionStorage.getItem(processKey)) {
      return;
    }
    
    const handleCallback = async () => {
      try {
        // 设置处理标记
        hasProcessed.current = true;
        sessionStorage.setItem(processKey, 'true');
        
        const error = searchParams.get('error');
        const state = searchParams.get('state');

        console.log('SSO回调处理开始:', { code, error, state });

        if (error) {
          throw new Error(`SSO登录失败: ${error}`);
        }

        setStatus('正在验证授权码...');
        console.log('开始处理授权码:', code);

        // 处理SSO回调，传递state参数用于CSRF验证
        const result = await ssoAuth.handleCallback(code, state);
        console.log('SSO回调结果:', result);

        if (result.success) {
          // 同步用户信息到现有的localStorage结构
          localStorage.setItem('token', result.token);
          
          if (result.user) {
            // 根据现有系统的用户信息结构设置
            localStorage.setItem('role', result.user.role || 'user');
            localStorage.setItem('accountType', result.user.accountType || 'main');
            
            // 保存SSO特有的信息
            if (result.user.isSSOUser) {
              localStorage.setItem('isSSOUser', 'true');
            }
            
            if (result.user.permissions) {
              localStorage.setItem('permissions', JSON.stringify(result.user.permissions));
            }
            
            if (result.user.parentUsername) {
              localStorage.setItem('parentUsername', result.user.parentUsername);
            }
          }

          // 只显示一次通知，使用sessionStorage防止重复
          if (!sessionStorage.getItem(notifyKey)) {
            sessionStorage.setItem(notifyKey, 'true');
            
            // 检查是否为新用户
            if (result.isNewUser) {
              setIsNewUser(true);
              notification.success({
                message: 'SSO登录成功',
                description: result.message || 'SSO登录成功，账号已自动创建',
                duration: 3,
                key: 'sso-login-success' // 使用key防止重复通知
              });
            } else {
              notification.success({
                message: 'SSO登录成功',
                description: result.message || 'SSO登录成功',
                duration: 2,
                key: 'sso-login-success' // 使用key防止重复通知
              });
            }
          }

          setStatus(result.isNewUser ? '账号已自动创建，正在跳转...' : '登录成功，正在跳转...');
          setLoading(false);

          // 延迟跳转，确保用户能看到成功状态
          const redirectDelay = result.isNewUser ? 2000 : 1500;
          setTimeout(() => {
            // 清除处理标记
            sessionStorage.removeItem(processKey);
            sessionStorage.removeItem(notifyKey);
            
            // 获取返回URL，如果有保存的返回地址则使用，否则根据用户角色跳转
            const returnUrl = sessionStorage.getItem('sso_return_url');
            if (returnUrl && returnUrl !== '/login') {
              sessionStorage.removeItem('sso_return_url'); // 清除返回URL
              navigate(returnUrl, { replace: true });
            } else {
              // 根据用户角色跳转到相应页面
              const userRole = result.user?.role || 'user';
              if (userRole === 'admin') {
                navigate('/admin/dashboard', { replace: true });
              } else if (userRole === 'sub') {
                navigate('/sub/dashboard', { replace: true });
              } else {
                navigate('/user/dashboard', { replace: true });
              }
            }
          }, redirectDelay);
        }

      } catch (err) {
        console.error('SSO回调处理失败:', err);
        setError(err.message);
        setStatus('登录失败');
        setLoading(false);
        
        // 只显示一次错误通知
        if (!sessionStorage.getItem(notifyKey)) {
          sessionStorage.setItem(notifyKey, 'true');
          notification.error({
            message: 'SSO登录失败',
            description: err.message,
            duration: 5,
            key: 'sso-login-error' // 使用key防止重复通知
          });
        }
        
        // 清除处理标记
        sessionStorage.removeItem(processKey);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const handleReturnToLogin = () => {
    // 清除所有SSO相关的临时数据
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('sso_processing_') || key.startsWith('sso_notified_')) {
        sessionStorage.removeItem(key);
      }
    });
    navigate('/login', { replace: true });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center',
          minWidth: '300px'
        }}>
          <Spin size="large" style={{ marginBottom: '1rem' }} />
          <h3 style={{ fontSize: '1.2rem', fontWeight: 500, marginBottom: '0.5rem' }}>{status}</h3>
          <p style={{ color: '#666', margin: 0 }}>
            {isNewUser ? '正在初始化您的账号...' : '请稍候...'}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center',
          minWidth: '300px'
        }}>
          <Result
            status="error"
            title="SSO登录失败"
            subTitle={error}
            extra={[
              <Button 
                type="primary" 
                onClick={handleReturnToLogin}
                key="return"
              >
                返回登录页面
              </Button>
            ]}
          />
        </div>
      </div>
    );
  }

  // 登录成功后只显示简单的加载状态，主要依赖notification显示成功信息
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        textAlign: 'center',
        minWidth: '300px'
      }}>
        <Spin size="large" style={{ marginBottom: '1rem' }} />
        <h3 style={{ fontSize: '1.2rem', fontWeight: 500, marginBottom: '0.5rem' }}>
          {isNewUser ? "账号创建成功" : "登录成功"}
        </h3>
        <p style={{ color: '#666', margin: 0 }}>{status}</p>
      </div>
    </div>
  );
};

export default SSOCallback; 